import React from "react";
import { View, Text } from "@react-pdf/renderer";
import { toExponentialUppercase } from "@app/utils/functions";
import { styles, ResultTable, OutputDataSection } from "./PDFComponents";
import { formatOutputValue } from "./dataProcessing";

/**
 * Helper to safely extract analysis code and description from dataObjects
 * Handles both direct objects and {label, value} format
 */
const getAnalysisInfo = (dataObjects: any) => {
  const typeAnalysis = dataObjects?.typeAnalysis;
  if (!typeAnalysis) return { codigo: '', descripcion: '' };
  
  // Handle {label, value} format
  if (typeAnalysis.value) {
    return {
      codigo: typeAnalysis.value,
      descripcion: typeAnalysis.label || ''
    };
  }
  
  // Handle direct object
  return {
    codigo: typeAnalysis?.codigo || '',
    descripcion: typeAnalysis?.descripcion || ''
  };
};

/**
 * Helper to safely extract calculation method
 * Handles both direct values and {label, value} format
 */
const getCalculationMethod = (dataObjects: any): string => {
  const method = dataObjects?.calculation_method;
  if (!method) return '';
  
  // Handle {label, value} format
  if (typeof method === 'object' && method.value) {
    return method.value;
  }
  
  // Handle direct string
  return method;
};

/**
 * Props para renderizar resultados y datos de salida
 */
interface RenderResultsProps {
  hasBothBTBR: boolean;
  hasBothSr89Sr90: boolean;
  allAnalysisResults: any;
  results: any[];
}

/**
 * Componente para renderizar la sección de resultados de cálculos
 */
export const ResultsSection: React.FC<RenderResultsProps> = ({
  hasBothBTBR,
  hasBothSr89Sr90,
  allAnalysisResults,
  results,
}) => {
  if (hasBothBTBR && allAnalysisResults) {
    return (
      <View style={{ marginBottom: 35 }}>
        <Text style={styles.heading}>Resultados Cálculos:</Text>
        <View style={{ flexDirection: "row", marginTop: 10, marginBottom: 5 }}>
          <View style={{ flex: 1, marginRight: 5 }}>
            <ResultTable
              title="Beta Total (BT)"
              results={allAnalysisResults.BT?.results?.map((result: any) => ({
                label: result.label,
                value: toExponentialUppercase(result.value, 4),
              }))}
            />
          </View>
          <View style={{ flex: 1, marginLeft: 5 }}>
            <ResultTable
              title="Beta Resto (BR)"
              results={allAnalysisResults.BR?.results?.map((result: any) => ({
                label: result.label,
                value: toExponentialUppercase(result.value, 4),
              }))}
            />
          </View>
        </View>
      </View>
    );
  }

  if (hasBothSr89Sr90 && allAnalysisResults) {
    return (
      <View>
        <Text style={styles.heading}>Resultados Cálculos:</Text>
        <View style={{ flexDirection: "row", gap: 15, marginTop: 10, marginBottom: 15 }} break>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 5 }}>
              Estroncio-89 (Sr89)
            </Text>
            <View style={styles.table}>
              {allAnalysisResults.Sr89?.results?.map((result: any, index: number) => (
                <View style={styles.tableRow} key={`sr89-${index}`}>
                  <Text style={styles.tableCellLabel}>{result.label}</Text>
                  <Text style={styles.tableCellValue}>
                    {toExponentialUppercase(result.value, 4)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 5 }}>
              Estroncio-90 (Sr90)
            </Text>
            <View style={styles.table}>
              {allAnalysisResults.Sr90?.results?.map((result: any, index: number) => (
                <View style={styles.tableRow} key={`sr90-${index}`}>
                  <Text style={styles.tableCellLabel}>{result.label}</Text>
                  <Text style={styles.tableCellValue}>
                    {toExponentialUppercase(result.value, 4)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View>
      <Text style={styles.heading}>Resultados Cálculos:</Text>
      <View style={{ ...styles.table, marginTop: 10 }}>
        {results.map((result, index) => (
          <View style={styles.tableRow} key={`result-${index}`}>
            <Text style={styles.tableCell}>{result.label}</Text>
            <Text style={styles.tableCell}>
              {toExponentialUppercase(result.value, 4)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

/**
 * Props para renderizar datos de salida
 */
interface OutputDataProps {
  hasBothBTBR: boolean;
  hasBothSr89Sr90: boolean;
  allAnalysisResults: any;
  arrrayKeys: any[];
  extendedLabelString: any;
  dataObjects: any;
}

/**
 * Componente para renderizar la sección de datos de salida
 */
export const OutputDataSections: React.FC<OutputDataProps> = ({
  hasBothBTBR,
  hasBothSr89Sr90,
  allAnalysisResults,
  arrrayKeys,
  extendedLabelString,
  dataObjects,
}) => {
  const analysisInfo = getAnalysisInfo(dataObjects);
  const calculationMethod = getCalculationMethod(dataObjects);
  
  if (hasBothBTBR && allAnalysisResults) {
    return (
      <View style={{...styles.optionalData, marginTop: 35}}>
        <View style={{ flexDirection: "row" }}>
          <View style={{ flex: 1, marginRight: 5 }}>
            <OutputDataSection
              outputData={allAnalysisResults.BT?.outputData}
              extendedLabels={allAnalysisResults.BT?.extendedLabels}
              title="Datos de salida - Beta Total (BT)"
              typeAnalysisCode={analysisInfo.codigo}
              calculationMethod={calculationMethod}
              formatOutputValue={formatOutputValue}
            />
          </View>
          <View style={{ flex: 1, marginLeft: 5 }}>
            <OutputDataSection
              outputData={allAnalysisResults.BR?.outputData}
              extendedLabels={allAnalysisResults.BR?.extendedLabels}
              title="Datos de salida - Beta Resto (BR)"
              typeAnalysisCode={analysisInfo.codigo}
              calculationMethod={calculationMethod}
              formatOutputValue={formatOutputValue}
            />
          </View>
        </View>
      </View>
    );
  }

  if (hasBothSr89Sr90 && allAnalysisResults) {
    return (
      <View wrap={false}>
        <View style={{ flexDirection: "row", gap: 15, marginTop: 10 }}>
          <View style={{ flex: 1 }}>
            <OutputDataSection
              outputData={allAnalysisResults.Sr89?.outputData}
              extendedLabels={allAnalysisResults.Sr89?.extendedLabels}
              title="Datos de salida - Estroncio-89 (Sr89)"
              typeAnalysisCode={analysisInfo.codigo}
              calculationMethod={calculationMethod}
              formatOutputValue={formatOutputValue}
            />
          </View>
          <View style={{ flex: 1 }}>
            <OutputDataSection
              outputData={allAnalysisResults.Sr90?.outputData}
              extendedLabels={allAnalysisResults.Sr90?.extendedLabels}
              title="Datos de salida - Estroncio-90 (Sr90)"
              typeAnalysisCode={analysisInfo.codigo}
              calculationMethod={calculationMethod}
              formatOutputValue={formatOutputValue}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.optionalData}>
      <OutputDataSection
        outputData={arrrayKeys}
        extendedLabels={extendedLabelString}
        title={`Datos de salida - ${analysisInfo.descripcion} `}
        typeAnalysisCode={analysisInfo.codigo}
        calculationMethod={calculationMethod}
        formatOutputValue={formatOutputValue}
      />
    </View>
  );
};
